import { useQuery } from "@tanstack/react-query";
import { timestampToLocalTime } from "anka-endeavour";
import { Button, Card, Checkbox, Col, DatePicker, Descriptions, Form, Input, Row, Select, Skeleton, Space, Tabs } from "antd";
import axios from "axios";
import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import DataTable from "../components/DataTable";
import MessageStatusModal from "../components/MessageStatusModal";
import Status from "../components/Status";
import UserContactDrawer from "../components/UserContactDrawer";
import { hostname } from "../hooks/useEndeavour";
import { set } from "@ant-design/plots/es/core/utils"; // unused import kept for potential future use

function UserPage() {
  const [forceUpdate, setForceUpdate] = useState(0);
  const modalRef = useRef();
  const drawerRef = useRef();
  const { userID } = useParams();
  const [messageStatus, setMessageStatus] = useState([]);
  const [selected, setSelected] = useState([]);
  const query = useQuery({
    queryKey: ["users", userID],
    queryFn: () => {
      return axios
        .get(`${hostname}/pa-alliance/user/${userID}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        })
        .then((res) => {
          return res.data.payload;
        });
    },
  });

  const statusQuery = useQuery({
    queryKey: ["userStatus", userID],
    queryFn: () => {
      return axios
        .get(`${hostname}/pa-alliance/user/status`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        })
        .then((res) => {
          setMessageStatus(() => {
            return res.data.payload
              .filter(item => item !== "Closed")
              .map(item => ({
                label: item,
                value: item,
              }));
          });

          return res.data.payload;
        }).catch((err) => {
          console.error("Error fetching user status:", err);
          return null;
        });

    }
  })

  if (query.isFetching) {
    return <Skeleton active />;
  }

  const onChange = () => {
    window.location.reload();
  };

  const onClickContact = (data) => {
    modalRef.current.openModal(data);
  };

  const onClickReplyCase = (data) => {
    drawerRef.current.openDrawer(data?.card_user_id, false, null, data?.id)
  }

  const onUpdateFeeGroup = () => {
    setForceUpdate(forceUpdate + 1);
  };

  const renderTab = (tab) => {
    switch (tab) {
      case "contact_log":
        return (
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <DataTable
                name="contact_log"
                ForceUpdate={forceUpdate}
                uri={`${hostname}/pa-alliance/user/contact`}
                filters={[
                  <Form.Item label="Subject" name="subject">
                    <Input />
                  </Form.Item>,
                  <Form.Item label="Content" name="content">
                    <Input />
                  </Form.Item>,
                  <Form.Item label="Date Range" name="created_at">
                    <DatePicker.RangePicker />
                  </Form.Item>,
                  <Form.Item label="Status" name="status">
                    <Checkbox.Group>
                      <Row>
                        {messageStatus.map((item, index) => (
                          <Col span={12} key={index}>
                            <Checkbox value={item.value}>{item.label}</Checkbox>
                          </Col>
                        ))}
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>,
                  <Form.Item name={"card_user_id"} hidden>
                    <Input />
                  </Form.Item>,
                ]}
                filtersInitialValues={{
                  card_user_id: query.data?.id,
                  status: statusQuery.data?.filter((item) => item !== 'Closed') || [],
                }}
                tableColumns={[
                  {
                    title: "Sender",
                    dataIndex: "sender",
                    key: "sender",
                  },
                  {
                    title: "To",
                    dataIndex: "to",
                    key: "to",
                  },
                  {
                    title: "Type",
                    dataIndex: "type",
                    key: "type",
                  },
                  {
                    title: "Conversation Status",
                    render: (data) => {
                      return data.conversation_status ? <Status value={data.conversation_status} /> : null;
                    },
                  },
                  {
                    title: "Status",
                    render: (data) => {
                      return <Status value={data.status} />;
                    },
                  },
                  {
                    title: "Subject",
                    dataIndex: "subject",
                    key: "subject",
                  },
                  {
                    title: "Content",
                    dataIndex: "content",
                    key: "content",
                  },
                  {
                    title: "Date",
                    dataIndex: "created_at",
                    key: "created_at",
                    render: (data) => timestampToLocalTime(data),
                  },
                  {
                    title: "Created By",
                    dataIndex: "created_by",
                    key: "created_by",
                  },
                  {
                    title: 'Case Id',
                    dataIndex: 'case_id',
                    key: 'case_id',
                  },
                  {
                    title: "Action",
                    render: (data) => {
                      return (<Space>
                        <Button type="primary" onClick={() => onClickReplyCase(data)}>
                          Reply
                        </Button>
                        <Button type="primary" onClick={() => onClickContact(data)}>
                          Update Status
                        </Button>
                      </Space>
                      );
                    },
                  },
                ]}
              />
            </Col>
          </Row>
        );
      case "closed_contact_log":
        return (
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <DataTable
                name="closed_contact_log"
                ForceUpdate={forceUpdate}
                uri={`${hostname}/pa-alliance/user/contact`}
                filters={[
                  <Form.Item label="Subject" name="subject">
                    <Input />
                  </Form.Item>,
                  <Form.Item label="Content" name="content">
                    <Input />
                  </Form.Item>,
                  <Form.Item label="Date Range" name="created_at">
                    <DatePicker.RangePicker />
                  </Form.Item>,
                  <Form.Item name={"card_user_id"} hidden>
                    <Input />
                  </Form.Item>,
                  <Form.Item name={"status"} hidden>
                    <Input />
                  </Form.Item>,
                ]}
                filtersInitialValues={{
                  card_user_id: query.data?.id,
                  status: "closed",
                }}
                tableColumns={[
                  {
                    title: "Sender",
                    dataIndex: "sender",
                    key: "sender",
                  },
                  {
                    title: "To",
                    dataIndex: "to",
                    key: "to",
                  },
                  {
                    title: "Type",
                    dataIndex: "type",
                    key: "type",
                  },
                  {
                    title: "Conversation Status",
                    render: (data) => {
                      return data.conversation_status ? <Status value={data.conversation_status} /> : null;
                    },
                  },
                  {
                    title: "Status",
                    render: (data) => {
                      return <Status value={data.status} />;
                    },
                  },
                  {
                    title: "Subject",
                    dataIndex: "subject",
                    key: "subject",
                  },
                  {
                    title: "Content",
                    dataIndex: "content",
                    key: "content",
                  },
                  {
                    title: "Date",
                    dataIndex: "created_at",
                    key: "created_at",
                    render: (data) => timestampToLocalTime(data),
                  },
                  {
                    title: "Created By",
                    dataIndex: "created_by",
                    key: "created_by",
                  },
                  {
                    title: 'Case Id',
                    dataIndex: 'case_id',
                    key: 'case_id',
                  },
                  {
                    title: "Action",
                    render: (data) => {
                      return (<Space>
                        <Button type="primary" onClick={() => onClickReplyCase(data)}>
                          Reply
                        </Button>
                        <Button type="primary" onClick={() => onClickContact(data)}>
                          Update Status
                        </Button>
                      </Space>
                      );
                    },
                  },
                ]}
              />
            </Col>
          </Row>
        );
      case "user_event":
        return (
          <DataTable
            uri={`${hostname}/pa-alliance/user/event`}
            filters={[
              <Form.Item label="Type" name="type">
                <Input />
              </Form.Item>,
              <Form.Item label="Date Range" name="created_at">
                <DatePicker.RangePicker />
              </Form.Item>,
              <Form.Item name={"card_user_id"} hidden>
                <Input />
              </Form.Item>,
            ]}
            filtersInitialValues={{
              card_user_id: query.data?.id,
            }}
            tableColumns={[
              {
                title: "Type",
                render: (data) => {
                  return data.description;
                },
              },
              {
                title: "Created Date",
                render: (data) => {
                  return timestampToLocalTime(data.created_at);
                },
              },
            ]}
          />
        );
    }
  };

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={12}>
        <Card title={"User Info"}>
          <Descriptions layout="vertical">
            <Descriptions.Item label="User">{query.data?.email}</Descriptions.Item>
            <Descriptions.Item label="Status">{query.data.status !== null && <Status value={query.data?.status} />}</Descriptions.Item>
            <Descriptions.Item label="Registration Time">{timestampToLocalTime(query.data?.created_at)}</Descriptions.Item>
            <Descriptions.Item label="Last Contact Time">{timestampToLocalTime(query.data?.contacted_at)}</Descriptions.Item>
            <Descriptions.Item label="Last 3 months transaction record ">
              <Status value={query.data?.transactions_3m} />
            </Descriptions.Item>

            <Descriptions.Item label="Contact Status">
              {query.data?.contact_status !== null && query.data?.contact_status !== undefined ? (
                <Status value={query.data?.contact_status} />
              ) : (
                <Status value={"pending"} />
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>
      <Col xs={24} md={12}>
        <Card title="Verification Status" style={{ height: "100%" }}>
          <Descriptions layout="vertical">
            <Descriptions.Item label="Email">
              <Status value={query.data?.email_verified === false ? "FAILED" : "SUCCESS"} />
            </Descriptions.Item>
            <Descriptions.Item label="Mobile">
              <Status value={query.data?.mobile_verified === false ? "FAILED" : "SUCCESS"} />
            </Descriptions.Item>
            <Descriptions.Item label="EKYC">
              <Status value={query.data?.kyc_id === null ? "FAILED" : "SUCCESS"} />
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>
      <Col span={24}>
        <Tabs
          items={[
            {
              key: "contact_log",
              label: "Contact Log",
              children: renderTab("contact_log"),
            },
            {
              key: "closed_contact_log",
              label: "Closed Contact Log",
              children: renderTab("closed_contact_log"),
            },
            {
              key: "user_event",
              label: "User Event",
              children: renderTab("user_event"),
            },
          ]}
          onTabClick={(key) => {
            // setForceUpdate(forceUpdate + 1);
            onUpdateFeeGroup();

          }}

        />
      </Col>

      <UserContactDrawer ref={drawerRef} onChange={onUpdateFeeGroup} setSelected={setSelected} />
      <MessageStatusModal ref={modalRef} onChange={onChange} />
    </Row>
  );
}

export default UserPage;
